import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import type { FileViolation } from "../../../../shared/core/data-forms/file-constraints";
import type { FileProps, FormImageInput } from "../../../../shared/core/data-forms/form-input-types";
import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { hasMultiplePages } from "../../../../shared/utils/form-input-image";
import type { UploadedImage } from "../../../../shared/utils/images-converter";
import { theme } from "../../styles/theme";
import { SingleImageField } from "./single-image-field";

const MAX_NB_PAGES = 20;

export const ImageField: React.FC<{
  input: FormImageInput;
  onChange: (picture: string | null, fileProps?: FileProps) => void;
  onMultiImagesChange: (values: (UploadedImage | null)[] | null) => void;
  onViolations?: (violations: FileViolation[]) => void;
  disabled?: boolean;
}> = ({ input, onChange, onMultiImagesChange, onViolations, disabled }) => {
  const { formatMessage } = useIntl();

  const hasSeveralPages = useMemo(() => hasMultiplePages(input), [input]);
  const [nbInputs, setNbInputs] = useState(input.minimumPageCount ?? 1);
  const [images, setImages] = useState<(UploadedImage | null)[]>([]);

  const imageKeys = useMemo(() => (nbInputs > 1 ? [...Array(nbInputs).keys()] : [0]), [nbInputs]);
  const showAddButton = useMemo(() => {
    const maxPages = input.maximumPageCount ?? MAX_NB_PAGES;
    return maxPages > nbInputs;
  }, [input, nbInputs]);

  const addImageInput = useCallback(() => {
    if (input.maximumPageCount && nbInputs < input.maximumPageCount) {
      setNbInputs(nbInputs + 1);
      setImages((prevImages) => [...prevImages, null]);
    }
  }, [input, nbInputs]);

  const handleImageChange = (image: UploadedImage | null, index: number) => {
    const updatedImages = [...images];
    updatedImages[index] = image;
    setImages(updatedImages);

    if (hasSeveralPages) {
      onMultiImagesChange(updatedImages);
    } else {
      onChange(image?.base64.split("base64,")?.[1] ?? null, image?.fileProps);
    }
  };

  const hasExistingFile = (key: number) => !!images[key];

  useEffect(() => {
    if (hasSeveralPages && images.length < nbInputs) {
      setImages((prevImages) => {
        const newImages = [...prevImages];
        while (newImages.length < nbInputs) {
          newImages.push(null); // only add
        }
        return newImages;
      });
    }
  }, [nbInputs, hasSeveralPages]);

  useEffect(() => {
    if (input.values) {
      setImages((input.values as (UploadedImage | null)[]).slice(0, MAX_NB_PAGES));
    } else if (input.value) {
      setImages([input.value as UploadedImage]);
    } else {
      setImages(Array(input.minimumPageCount ?? 1).fill(null));
    }
  }, [input.values, input.value]);

  const isRequired = () => {
    const { minimumPageCount } = input;
    const imageCount = images.filter(Boolean).length;
    if (minimumPageCount) {
      return imageCount < minimumPageCount;
    }
    return input.required;
  };

  return (
    <>
      {imageKeys.map((key, index) => (
        <div key={key} style={{ marginTop: 2, marginBottom: 8 }}>
          <SingleImageField
            onChange={(uploadedImage) => handleImageChange(uploadedImage, index)}
            onViolations={onViolations}
            disabled={disabled}
            input={{ ...input, required: isRequired() }}
            hasExistingFile={hasExistingFile(key)}
            value={images[index]?.base64 ?? images[index]?.value ?? (index === 0 ? input.value : undefined)}
            fileProps={images[index]?.fileProps ?? (index === 0 ? input.fileProps : undefined)}
            index={hasSeveralPages ? index + 1 : undefined}
          />
        </div>
      ))}
      {showAddButton ? (
        <AddPictureContainer onClick={addImageInput}>
          <AddPictureWrapper>
            <>
              <PlusTitle>{"＋"}</PlusTitle>
              <AddPictureTitle>{formatMessage("registerScreen.addPictureButton")}</AddPictureTitle>
            </>
          </AddPictureWrapper>
        </AddPictureContainer>
      ) : null}
    </>
  );
};

const AddPictureContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f4f4f4;
  margin-top: 16px;
  border-color: #bfbfbf;
  border-style: dashed;
  border-radius: 11px;
  border-width: 1.5px;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  &:hover {
    cursor: pointer;
  }
`;

const AddPictureWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const AddPictureTitle = styled.span`
  font-size: 0.85rem;
  ${theme.mediumText};
  margin: 0 4px;
`;

const PlusTitle = styled.span`
  margin: 0 4px;
  font-size: 0.875rem;
  ${theme.boldText};
`;
